import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import { supabase } from '../config/supabase';
import { getAdditionalRidersNeeded } from '../config/sharedTransferRules';

const FallbackPromptModal = ({
  isOpen,
  onClose,
  trip,
  participantId,
  onAccept,
  onDecline
}) => {
  const [countdown, setCountdown] = useState(900);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleDecline = useCallback(async () => {
    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('participants')
        .update({ accepted_fallback: false })
        .eq('id', participantId);
      if (error) console.error('Error declining fallback:', error);
      onDecline();
    } catch (error) {
      console.error('Error declining fallback:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [participantId, onDecline]);

  useEffect(() => {
    if (!isOpen || countdown <= 0) return;
    const timer = setInterval(() => setCountdown(prev => prev - 1), 1000);
    return () => clearInterval(timer);
  }, [isOpen, countdown]);

  useEffect(() => {
    if (countdown <= 0 && isOpen) handleDecline();
  }, [countdown, isOpen, handleDecline]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const currentSeats = trip.seats_filled || 0;
  const minSeats = trip.min_seat_count || 2;
  const ridersNeeded = getAdditionalRidersNeeded(currentSeats, minSeats);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-[#2D1E17]/60 backdrop-blur-sm" />
      <div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden">
        <div className="p-8 text-center">
          {/* Countdown */}
          <div className="mb-6">
            <div className="text-3xl font-serif font-bold text-[#A44432]">{formatTime(countdown)}</div>
            <div className="text-xs text-stone-400 uppercase tracking-widest font-bold mt-1">Time remaining to decide</div>
          </div>

          {/* Icon */}
          <div className="w-16 h-16 bg-orange-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <AlertTriangle size={32} className="text-orange-500" />
          </div>

          <h2 className="text-xl font-serif font-bold text-[#2D1E17] mb-4">Trip Waiting for Participants</h2>

          <p className="text-sm text-stone-500 mb-6">
            This trip from <strong className="text-[#2D1E17]">{trip.pickup_location}</strong> to{' '}
            <strong className="text-[#2D1E17]">{trip.dropoff_location}</strong> on{' '}
            <strong className="text-[#2D1E17]">{new Date(trip.travel_date).toLocaleDateString()}</strong>{' '}
            is still waiting to be filled.
          </p>

          {/* Seat stats */}
          <div className="bg-stone-50 rounded-2xl p-4 mb-6 border border-stone-100">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-stone-500">Riders</span><span className="font-bold">{currentSeats} of {minSeats}</span></div>
              <div className="flex justify-between"><span className="text-stone-500">Still needed</span><span className="font-bold text-[#A44432]">{ridersNeeded} more</span></div>
            </div>
          </div>

          {/* Payment info */}
          <div className="bg-[#FAF3E0] border border-[#E8DED1] rounded-2xl p-4 mb-6 text-left">
            <p className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-2">Payment Info</p>
            <ul className="text-xs text-stone-600 space-y-1">
              <li>Your card will be authorized for the amount shown</li>
              <li>No charges until the trip is confirmed</li>
              <li>If more people join, your charge will be reduced</li>
            </ul>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={onAccept}
              disabled={isSubmitting}
              className="flex-1 bg-[#2D5A27] text-white px-4 py-3 rounded-xl font-medium hover:bg-green-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <CheckCircle2 size={16} /> Accept &amp; Proceed
            </button>
            <button
              onClick={handleDecline}
              disabled={isSubmitting}
              className="flex-1 btn-danger py-3"
            >
              <XCircle size={16} /> Cancel Trip
            </button>
          </div>

          <div className="mt-4 text-[10px] text-stone-400 space-y-0.5">
            <p>Accepting authorizes your card at the higher rate</p>
            <p>If you don&apos;t respond, the trip will be cancelled automatically</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FallbackPromptModal;
